/**
 * Native Mexican-American Spanish (es-US) copy for Fort Worth Dental's top 6
 * conversion-priority service pages. Every term has been checked against the
 * dental glossary from FORTWORTHDENTAL_BILINGUAL_BRIEF.md.
 *
 * Do NOT machine-translate generic dental terms — use "endodoncia" (not
 * "tratamiento de conducto"), "blanqueamiento dental" (not "blanqueamiento
 * de dientes"), "limpieza dental" (not "limpieza de dientes"), etc.
 *
 * Ordering below follows the conversion-impact ranking:
 *   1. Implantes   — highest-value transaction
 *   2. Emergencia  — highest urgency → fastest close
 *   3. Invisalign  — high-intent in Spanish-speaking households
 *   4. Blanqueamiento — high search volume
 *   5. Odontología Cosmética — brand halo
 *   6. Endodoncia  — high search volume, anxious buyer
 */

export const SPANISH_SERVICES = {
  'implantes-dentales': {
    slug: 'implantes-dentales',
    esPath: '/es/implantes-dentales',
    enPath: '/dental-implants',
    metaTitle: 'Implantes Dentales en Fort Worth, TX | Fort Worth Dental',
    metaDescription:
      'Implantes dentales permanentes en Fort Worth, TX. Dr. Mund es Diplomado del American Board of Oral Implantology. Consulta gratis. Financiamiento disponible.',
    schemaName: 'Implantes Dentales',
    h1: 'Implantes Dentales en Fort Worth',
    subtitle:
      'Recupera tu sonrisa con implantes dentales que se ven y se sienten completamente naturales. Colocados por el Dr. Mund, especialista certificado con más de 25 años de experiencia.',
    image: '/images/services/frame-1279-4.png',
    imageAlt: 'Diagrama de un implante dental mostrando el poste de titanio, el pilar y la corona de porcelana — Fort Worth Dental',
    introHeadline: '¿Por qué elegir implantes dentales?',
    introParagraphs: [
      'Los implantes dentales son una de las soluciones más transformadoras en odontología moderna. Son la opción ideal para pacientes a quienes les falta uno o varios dientes.',
      'Reemplazar los dientes perdidos con implantes de apariencia natural devuelve la confianza para sonreír, reír y comer en público. Muchos pacientes que antes evitaban ser el centro de atención recuperan su vida social por completo.',
    ],
    benefitsHeadline: '¿Cuáles son las ventajas de los implantes dentales?',
    benefits: [
      { title: 'Duraderos', body: 'Los implantes dentales duran décadas y ofrecen soporte continuo para tu mandíbula.' },
      { title: 'Cómodos', body: 'Se sienten mucho más cómodos y naturales que las dentaduras postizas.' },
      { title: 'Apariencia natural', body: 'Se ven exactamente como dientes reales.' },
      { title: 'Preservan el hueso', body: 'Ayudan a mantener la estructura facial y previenen la pérdida ósea.' },
      { title: 'Funcionalidad completa', body: 'Permiten comer, hablar y sonreír con total libertad.' },
      { title: 'Higiene sencilla', body: 'Se cepillan exactamente igual que los dientes naturales.' },
    ],
    faq: [
      {
        q: '¿Cuánto cuesta un implante dental en Fort Worth?',
        a: 'El costo varía según el caso — número de implantes, necesidad de injerto óseo y tipo de restauración. En lugar de publicar precios que pueden no aplicar a tu situación, te damos una cotización personalizada por escrito en tu consulta GRATIS (incluye radiografías 3D — valor de $475). Financiamiento disponible para todos los presupuestos.',
      },
      {
        q: '¿Los implantes duelen?',
        a: 'El procedimiento se realiza bajo anestesia local y sedación si lo prefieres — la mayoría de pacientes reportan molestias mínimas, comparable a una extracción simple. El Dr. Mund ha colocado más de 10,000 implantes.',
      },
      {
        q: '¿Aceptan mi seguro dental?',
        a: 'Aceptamos la mayoría de seguros dentales mayores (Delta Dental, Cigna, Aetna, MetLife, Humana y más). Verificamos tu cobertura sin costo antes de empezar.',
      },
    ],
    ctaHeadline: 'Consulta GRATIS de implantes dentales',
    ctaBody:
      'Agenda tu consulta inicial sin costo y recibe radiografías 3D y un plan de tratamiento personalizado — valor total de $475, totalmente GRATIS para nuevos pacientes.',
  },

  'dentista-de-emergencia': {
    slug: 'dentista-de-emergencia',
    esPath: '/es/dentista-de-emergencia',
    enPath: '/emergency-dentist',
    metaTitle: 'Dentista de Emergencia en Fort Worth | Citas el Mismo Día',
    metaDescription:
      'Emergencia dental en Fort Worth — citas el mismo día. Dolor de muela, diente roto, infección. Atendemos sin cita previa. Llama al (817) 985-3337.',
    schemaName: 'Dentista de Emergencia',
    h1: 'Emergencia Dental en Fort Worth',
    subtitle:
      'Dolor de muela, diente roto, infección o trauma dental — atendemos emergencias el mismo día. No esperes más tiempo sufriendo: estamos aquí para ayudarte ahora.',
    image: '/images/services/emergency-dental.jpg',
    imageAlt: 'Dentista de emergencia atendiendo a un paciente con dolor de muela en Fort Worth',
    introHeadline: '¿Qué cuenta como emergencia dental?',
    introParagraphs: [
      'Una emergencia dental es cualquier situación que requiere atención inmediata para aliviar dolor severo, detener sangrado o salvar un diente. No ignores estos síntomas — mientras más esperes, más complicado (y costoso) se vuelve el tratamiento.',
      'En Fort Worth Dental reservamos espacios todos los días específicamente para pacientes de emergencia. Si llamas temprano, generalmente te atendemos el mismo día.',
    ],
    benefitsHeadline: 'Atendemos todas estas emergencias:',
    benefits: [
      { title: 'Dolor de muela intenso', body: 'Puede indicar infección, caries profunda o daño en el nervio — requiere evaluación inmediata.' },
      { title: 'Diente roto o astillado', body: 'Reparación el mismo día con coronas, empastes o carillas.' },
      { title: 'Diente caído o golpeado', body: 'Si actúas en las primeras 2 horas, podemos reimplantar el diente.' },
      { title: 'Absceso o infección', body: 'Tratamiento urgente con antibióticos y drenaje para evitar complicaciones.' },
      { title: 'Corona o empaste perdido', body: 'Recolocación o reemplazo rápido para proteger el diente.' },
      { title: 'Sangrado excesivo de las encías', body: 'Evaluación inmediata — podría ser enfermedad de las encías avanzada.' },
    ],
    faq: [
      {
        q: '¿Pueden atenderme hoy?',
        a: 'Sí. Reservamos espacios diarios para emergencias. Llama al (817) 985-3337 ahora y generalmente te atendemos el mismo día o al día siguiente máximo.',
      },
      {
        q: '¿Cuánto cuesta una emergencia dental?',
        a: 'Nuestro examen de emergencia con radiografía cuesta solo $99 para nuevos pacientes. El tratamiento posterior depende del diagnóstico — te damos el estimado antes de proceder.',
      },
      {
        q: '¿Qué hago mientras llego al consultorio?',
        a: 'Para dolor: ibuprofeno (no aspirina si hay sangrado) y compresas frías. Para diente caído: guárdalo en leche o saliva, no lo enjuagues. Para sangrado: muerde una gasa limpia durante 15 minutos.',
      },
    ],
    ctaHeadline: 'No esperes — llama ahora',
    ctaBody:
      'El dolor dental empeora con el tiempo. Llámanos inmediatamente y te damos orientación por teléfono mientras programamos tu cita de emergencia.',
  },

  'invisalign': {
    slug: 'invisalign',
    esPath: '/es/invisalign',
    enPath: '/invisalign',
    metaTitle: 'Invisalign en Fort Worth | Alineadores Transparentes | Fort Worth Dental',
    metaDescription:
      'Endereza tus dientes discretamente con Invisalign en Fort Worth. Alineadores transparentes hechos a la medida. Consulta gratis. Planes de pago disponibles.',
    schemaName: 'Invisalign',
    h1: 'Invisalign — Alineadores Transparentes',
    subtitle:
      'Endereza tus dientes sin frenos tradicionales. Invisalign usa alineadores transparentes hechos a tu medida para una sonrisa hermosa, sin que nadie lo note.',
    image: '/images/services/invisalign-hero.jpg',
    imageAlt: 'Paciente sosteniendo alineadores transparentes Invisalign en Fort Worth Dental',
    introHeadline: '¿Por qué elegir Invisalign sobre los frenos tradicionales?',
    introParagraphs: [
      'Invisalign es el sistema de alineadores más reconocido del mundo. A diferencia de los frenos metálicos, los alineadores Invisalign son prácticamente invisibles, removibles, y no tienen alambres que irriten tus mejillas.',
      'Perfecto para adultos profesionales y adolescentes que quieren una sonrisa recta sin la incomodidad estética de los frenos tradicionales.',
    ],
    benefitsHeadline: 'Ventajas de Invisalign',
    benefits: [
      { title: 'Casi invisibles', body: 'Nadie notará que estás en tratamiento — ideal para trabajo, escuela o eventos sociales.' },
      { title: 'Removibles', body: 'Te los quitas para comer y cepillarte — higiene dental normal sin restricciones.' },
      { title: 'Cómodos', body: 'Sin alambres ni brackets que lastimen tus mejillas o encías.' },
      { title: 'Menos visitas al dentista', body: 'Cambias los alineadores en casa cada 1-2 semanas. Revisiones cada 6-8 semanas.' },
      { title: 'Tratamiento más rápido', body: 'La mayoría de casos se completan en 12-18 meses — más rápido que frenos tradicionales.' },
      { title: 'Vista previa digital', body: 'Te mostramos cómo lucirá tu sonrisa final ANTES de comenzar el tratamiento.' },
    ],
    faq: [
      {
        q: '¿Cuánto cuesta Invisalign en Fort Worth?',
        a: 'Cada caso de Invisalign es único — el costo depende de la complejidad de tu mordida y la duración del tratamiento. Te damos una cotización personalizada por escrito en tu consulta GRATIS de Invisalign. Planes de pago mensuales y financiamiento sin interés disponibles.',
      },
      {
        q: '¿Cubre Invisalign mi seguro dental?',
        a: 'Muchos planes de seguro dental cubren Invisalign como cobertura de ortodoncia. Verificamos tu cobertura sin costo y te decimos exactamente cuánto pagarás de tu bolsillo.',
      },
      {
        q: '¿Duele usar Invisalign?',
        a: 'Puedes sentir presión leve los primeros 2-3 días con cada nuevo alineador — es normal y significa que está funcionando. Mucho menos doloroso que frenos tradicionales.',
      },
    ],
    ctaHeadline: 'Consulta Invisalign GRATIS',
    ctaBody:
      'Descubre si Invisalign es la opción correcta para ti. Escaneo digital gratuito, vista previa de tu sonrisa final, y estimado de costos sin compromiso.',
  },

  'blanqueamiento-dental': {
    slug: 'blanqueamiento-dental',
    esPath: '/es/blanqueamiento-dental',
    enPath: '/teeth-whitening',
    metaTitle: 'Blanqueamiento Dental en Fort Worth | Sonrisa Más Blanca',
    metaDescription:
      'Blanqueamiento dental profesional en Fort Worth. Sonrisa más blanca en una sola visita. Método seguro y efectivo. Consulta gratis.',
    schemaName: 'Blanqueamiento Dental',
    h1: 'Blanqueamiento Dental en Fort Worth',
    subtitle:
      'Una sonrisa más blanca en una sola visita — hasta 8 tonos más claros. Blanqueamiento dental profesional, seguro y efectivo.',
    image: '/images/services/teeth-whitening.jpg',
    imageAlt: 'Comparación antes y después de blanqueamiento dental en Fort Worth Dental',
    introHeadline: '¿Por qué blanquear con un dentista en lugar de kits de farmacia?',
    introParagraphs: [
      'Los kits de blanqueamiento de farmacia son débiles y pueden irritar tus encías o dañar el esmalte si se usan incorrectamente. El blanqueamiento profesional usa una concentración más alta de peróxido de manera segura, con protección personalizada para tus encías.',
      'Resultados visibles en una sola cita de 1 hora — lo que tomaría semanas con kits caseros lo hacemos mientras tomas una siesta.',
    ],
    benefitsHeadline: 'Beneficios del blanqueamiento profesional',
    benefits: [
      { title: 'Resultados rápidos', body: 'Hasta 8 tonos más blanco en una sola visita de 60 minutos.' },
      { title: 'Seguro y supervisado', body: 'Un dentista certificado protege tus encías y monitorea todo el proceso.' },
      { title: 'Duradero', body: 'Con buen mantenimiento los resultados duran 2-3 años.' },
      { title: 'Personalizado', body: 'Ajustamos la concentración al tono de tu esmalte y sensibilidad.' },
      { title: 'Sin daño al esmalte', body: 'Técnica profesional que no debilita los dientes.' },
    ],
    faq: [
      {
        q: '¿Cuánto cuesta el blanqueamiento dental?',
        a: 'El costo varía según el tono actual de tus dientes y el método (en consultorio o kit profesional para casa). Ofrecemos consulta gratis para determinar el mejor método según tu caso — sin compromiso.',
      },
      {
        q: '¿Duele el blanqueamiento?',
        a: 'No. Puedes sentir sensibilidad temporal los primeros 1-2 días — usamos un gel desensibilizante al final del tratamiento para minimizarlo.',
      },
      {
        q: '¿Funciona en coronas o carillas?',
        a: 'El blanqueamiento solo funciona en esmalte natural. Si tienes coronas o carillas frontales, lo evaluamos primero para recomendarte la mejor opción.',
      },
    ],
    ctaHeadline: 'Sonrisa más blanca hoy mismo',
    ctaBody:
      'Agenda tu cita de blanqueamiento profesional. Consulta inicial gratuita — en menos de 1 hora puedes salir con una sonrisa notablemente más brillante.',
  },

  'odontologia-cosmetica': {
    slug: 'odontologia-cosmetica',
    esPath: '/es/odontologia-cosmetica',
    enPath: '/cosmetic-dentistry',
    metaTitle: 'Odontología Cosmética en Fort Worth | Diseño de Sonrisa',
    metaDescription:
      'Transforma tu sonrisa con odontología cosmética en Fort Worth. Carillas dentales, blanqueamiento, bondings y diseño de sonrisa completo. Consulta gratis.',
    schemaName: 'Odontología Cosmética',
    h1: 'Odontología Cosmética',
    subtitle:
      'Transforma tu sonrisa con carillas de porcelana, blanqueamiento profesional, bondings cosméticos y diseño de sonrisa completo.',
    image: '/images/services/cosmetic-dentistry.jpg',
    imageAlt: 'Transformación de sonrisa con odontología cosmética en Fort Worth',
    introHeadline: 'Tu sonrisa es una inversión de por vida',
    introParagraphs: [
      'La odontología cosmética combina arte y ciencia para crear la sonrisa que siempre quisiste. Desde pequeños ajustes hasta transformaciones completas, usamos las técnicas más modernas para resultados naturales y duraderos.',
      'Cada tratamiento se diseña específicamente para tu rostro, tu tono de piel y tus rasgos — no hay dos sonrisas iguales porque no hay dos personas iguales.',
    ],
    benefitsHeadline: 'Servicios cosméticos disponibles',
    benefits: [
      { title: 'Carillas de porcelana', body: 'Láminas delgadas que transforman la forma, color y tamaño de tus dientes.' },
      { title: 'Carillas First Fit', body: 'Nueva tecnología — sonrisa completa en solo 2 horas, sin múltiples visitas.' },
      { title: 'Blanqueamiento profesional', body: 'Hasta 8 tonos más blanco en una sola visita.' },
      { title: 'Bondings cosméticos', body: 'Reparación económica para chips, grietas o espacios pequeños.' },
      { title: 'Diseño digital de sonrisa', body: 'Te mostramos cómo lucirá tu sonrisa final antes de comenzar.' },
      { title: 'Coronas cosméticas', body: 'Restauraciones que se ven idénticas a dientes naturales.' },
    ],
    ctaHeadline: 'Consulta gratuita de diseño de sonrisa',
    ctaBody:
      'Descubre qué tratamiento cosmético es ideal para ti. Consulta inicial gratuita con vista previa digital de tu sonrisa final.',
  },

  'endodoncia': {
    slug: 'endodoncia',
    esPath: '/es/endodoncia',
    enPath: '/root-canals',
    metaTitle: 'Endodoncia en Fort Worth | Tratamiento de Conducto Sin Dolor',
    metaDescription:
      'Endodoncia moderna y sin dolor en Fort Worth. Salva tu diente natural. Citas el mismo día disponibles para dolor severo.',
    schemaName: 'Endodoncia',
    h1: 'Endodoncia en Fort Worth',
    subtitle:
      'Salva tu diente natural con endodoncia moderna. Técnicas suaves, anestesia avanzada y sedación disponible para una experiencia sin dolor.',
    image: '/images/services/root-canal.jpg',
    imageAlt: 'Diagrama de endodoncia mostrando la limpieza del conducto radicular en Fort Worth Dental',
    introHeadline: 'La endodoncia ya no es lo que era',
    introParagraphs: [
      'La fama de que la endodoncia es dolorosa viene de hace décadas — hoy, con anestesia moderna y técnicas avanzadas, el procedimiento es casi indistinguible de un empaste común. La mayoría de pacientes nos dicen: "¿Ya terminó?"',
      'El objetivo es salvar tu diente natural en lugar de extraerlo. Un diente natural, incluso con endodoncia, dura más y funciona mejor que cualquier reemplazo artificial.',
    ],
    benefitsHeadline: '¿Cuándo necesitas una endodoncia?',
    benefits: [
      { title: 'Dolor de muela persistente', body: 'Especialmente al masticar o aplicar presión.' },
      { title: 'Sensibilidad prolongada', body: 'Al frío o calor, incluso después de retirar el estímulo.' },
      { title: 'Oscurecimiento del diente', body: 'Puede indicar muerte del nervio interno.' },
      { title: 'Inflamación de las encías', body: 'Cerca del diente afectado — posible infección.' },
      { title: 'Absceso o bulto dental', body: 'Señal de infección avanzada — requiere tratamiento urgente.' },
      { title: 'Trauma dental', body: 'Un golpe severo puede dañar el nervio aunque el diente se vea intacto.' },
    ],
    faq: [
      {
        q: '¿Cuánto cuesta una endodoncia?',
        a: 'Varía según el diente (los molares son más complejos) y si necesitas una corona después. Te damos una cotización exacta antes de comenzar. Aceptamos la mayoría de seguros dentales y ofrecemos financiamiento.',
      },
      {
        q: '¿Duele la endodoncia?',
        a: 'No. Usamos anestesia local avanzada — la mayoría de pacientes reportan que es comparable a un empaste normal. También ofrecemos sedación oral para pacientes ansiosos.',
      },
      {
        q: '¿Cuánto dura una endodoncia?',
        a: 'Un diente tratado con endodoncia y cubierto con corona puede durar toda la vida con buen cuidado — igual que cualquier otro diente natural.',
      },
    ],
    ctaHeadline: '¿Tienes dolor de muela? Llámanos hoy',
    ctaBody:
      'No esperes a que empeore. Ofrecemos citas el mismo día para dolor dental severo. Diagnóstico gratuito y estimado claro antes de proceder.',
  },

  // -----------------------------------------------------------------
  // Phase 2 Batch 2 — remaining 18 service pages (concise but native ES)
  // -----------------------------------------------------------------

  'injerto-de-hueso': {
    slug: 'injerto-de-hueso', esPath: '/es/injerto-de-hueso', enPath: '/bone-grafting',
    metaTitle: 'Injerto de Hueso Dental en Fort Worth | Fort Worth Dental',
    metaDescription: 'Procedimiento de injerto de hueso para preparar tu mandíbula para implantes dentales. Mínimamente invasivo, recuperación rápida.',
    schemaName: 'Injerto de Hueso',
    h1: 'Injerto de Hueso Dental',
    subtitle: 'Cuando falta hueso en la mandíbula, el injerto de hueso restaura el volumen necesario para colocar implantes dentales con éxito.',
    introHeadline: '¿Por qué necesitas un injerto de hueso?',
    introParagraphs: [
      'La pérdida ósea en la mandíbula ocurre naturalmente cuando se pierden dientes. Sin estímulo, el hueso se reabsorbe con el tiempo.',
      'El injerto de hueso restaura ese volumen para poder colocar un implante dental estable que dure décadas. Usamos técnicas avanzadas mínimamente invasivas.',
    ],
    benefits: [
      { title: 'Permite implantes', body: 'Crea la base ósea necesaria para colocar implantes dentales exitosos.' },
      { title: 'Preserva la estructura facial', body: 'Evita el aspecto hundido que causa la pérdida ósea.' },
      { title: 'Mínimamente invasivo', body: 'Técnicas modernas con recuperación más rápida y menos molestias.' },
    ],
    ctaHeadline: '¿Estás considerando implantes?', ctaBody: 'Agenda una consulta gratuita para evaluar si necesitas injerto de hueso antes del implante.',
  },

  'bruxismo': {
    slug: 'bruxismo', esPath: '/es/bruxismo', enPath: '/bruxism-teeth-grinding',
    metaTitle: 'Tratamiento de Bruxismo en Fort Worth | Rechinar de Dientes',
    metaDescription: 'Tratamiento profesional para el bruxismo (rechinar de dientes). Protectores nocturnos personalizados. Consulta hoy.',
    schemaName: 'Tratamiento de Bruxismo',
    h1: 'Bruxismo (Rechinar de Dientes)',
    subtitle: 'El rechinar de dientes desgasta el esmalte y causa dolor de mandíbula. Tratamiento personalizado para detener el daño.',
    introHeadline: '¿Sufres de bruxismo?',
    introParagraphs: [
      'El bruxismo es el rechinar o apretar de los dientes, generalmente durante la noche. Si te despiertas con dolor de mandíbula o cabeza, es probable que tengas bruxismo.',
      'Sin tratamiento, el bruxismo causa fracturas, desgaste del esmalte, dolor crónico de mandíbula (TMJ) y migrañas.',
    ],
    benefits: [
      { title: 'Protectores nocturnos personalizados', body: 'Hechos a la medida — más cómodos y efectivos que los de farmacia.' },
      { title: 'Alivio del dolor', body: 'Reduce el dolor de mandíbula, cabeza y oído asociado al bruxismo.' },
      { title: 'Protege el esmalte', body: 'Evita fracturas y desgaste prematuro de los dientes.' },
    ],
    ctaHeadline: 'Detén el daño del bruxismo', ctaBody: 'Una consulta breve y un protector nocturno personalizado pueden cambiar tu calidad de vida.',
  },

  'empastes': {
    slug: 'empastes', esPath: '/es/empastes', enPath: '/composite-fillings',
    metaTitle: 'Empastes Dentales del Color del Diente | Fort Worth Dental',
    metaDescription: 'Empastes de resina compuesta del color natural del diente. Sin metales visibles. Atención el mismo día.',
    schemaName: 'Empastes Dentales',
    h1: 'Empastes Dentales',
    subtitle: 'Empastes de resina compuesta del color natural del diente — sin metales visibles, restauración invisible.',
    introHeadline: 'Empastes que se ven naturales',
    introParagraphs: [
      'Los empastes modernos de resina compuesta se ven exactamente como tu diente natural. Adiós a los empastes plateados visibles al sonreír.',
      'Tratamiento rápido en una sola visita — la mayoría de empastes se completan en 30-45 minutos.',
    ],
    benefits: [
      { title: 'Estética invisible', body: 'El material se mezcla perfectamente con el color de tu diente.' },
      { title: 'Sin mercurio', body: 'Resinas compuestas modernas, libres de metales tóxicos.' },
      { title: 'Cita única', body: 'Tratamiento completo en una sola visita.' },
    ],
    ctaHeadline: '¿Tienes una caries?', ctaBody: 'Tratamiento rápido y estético — agenda hoy y resuélvelo en una sola visita.',
  },

  'coronas-y-puentes': {
    slug: 'coronas-y-puentes', esPath: '/es/coronas-y-puentes', enPath: '/crowns-bridges',
    metaTitle: 'Coronas y Puentes Dentales en Fort Worth | Fort Worth Dental',
    metaDescription: 'Coronas de porcelana y puentes dentales para restaurar dientes dañados o reemplazar piezas perdidas. Apariencia 100% natural.',
    schemaName: 'Coronas y Puentes',
    h1: 'Coronas y Puentes Dentales',
    subtitle: 'Restaura dientes dañados con coronas de porcelana, o reemplaza dientes perdidos con puentes fijos — todo con apariencia natural.',
    introHeadline: 'Restauración duradera y estética',
    introParagraphs: [
      'Las coronas cubren un diente dañado completamente, devolviéndole su forma, fuerza y apariencia. Los puentes reemplazan dientes faltantes anclándose a los dientes vecinos.',
      'Usamos porcelana de alta gama que se ve idéntica al esmalte natural — incluso bajo luz directa.',
    ],
    benefits: [
      { title: 'Larga duración', body: 'Coronas y puentes de porcelana duran 15-20 años con buen cuidado.' },
      { title: 'Apariencia natural', body: 'Color, traslucidez y brillo idénticos a los dientes naturales.' },
      { title: 'Restaura la función', body: 'Vuelve a masticar con confianza sin ajustes ni molestias.' },
    ],
    ctaHeadline: 'Restaura tu sonrisa', ctaBody: 'Consulta gratuita para evaluar si una corona o puente es la mejor opción para ti.',
  },

  'dentaduras-postizas': {
    slug: 'dentaduras-postizas', esPath: '/es/dentaduras-postizas', enPath: '/dentures',
    metaTitle: 'Dentaduras Postizas en Fort Worth | Fort Worth Dental',
    metaDescription: 'Dentaduras postizas tradicionales y dentaduras sobre implantes. Restauración completa con apariencia natural y ajuste cómodo.',
    schemaName: 'Dentaduras Postizas',
    h1: 'Dentaduras Postizas',
    subtitle: 'Recupera la confianza de sonreír con dentaduras postizas modernas — tradicionales o sobre implantes para mayor estabilidad.',
    introHeadline: 'Una solución para cada presupuesto',
    introParagraphs: [
      'Las dentaduras modernas son cómodas, naturales y mucho más estables que las que recuerdas. Disponibles como completas, parciales, o ancladas a implantes para máxima estabilidad.',
      'Te ayudamos a elegir la opción ideal según tu salud bucal, presupuesto y estilo de vida.',
    ],
    benefits: [
      { title: 'Apariencia natural', body: 'Dentaduras hechas a la medida que se ven como dientes propios.' },
      { title: 'Mayor estabilidad', body: 'Dentaduras sobre implantes eliminan el deslizamiento.' },
      { title: 'Restaura la función', body: 'Vuelve a comer todos tus alimentos favoritos sin restricciones.' },
    ],
    ctaHeadline: 'Recupera tu sonrisa completa', ctaBody: 'Consulta gratis para conocer tus opciones de dentadura — tradicional o sobre implantes.',
  },

  'gingivectomia': {
    slug: 'gingivectomia', esPath: '/es/gingivectomia', enPath: '/gingivectomy',
    metaTitle: 'Gingivectomía en Fort Worth | Cirugía de Encías',
    metaDescription: 'Procedimiento de gingivectomía para tratar enfermedad de las encías o corregir sonrisa gingival. Mínimamente invasivo.',
    schemaName: 'Gingivectomía',
    h1: 'Gingivectomía',
    subtitle: 'Procedimiento que remueve tejido gingival enfermo o exceso de encía para mejorar la salud bucal o la estética de tu sonrisa.',
    introHeadline: '¿Cuándo se necesita una gingivectomía?',
    introParagraphs: [
      'Se recomienda cuando hay enfermedad de las encías avanzada con bolsas profundas, o por razones cosméticas si tus encías cubren demasiado los dientes ("sonrisa gingival").',
      'Procedimiento mínimamente invasivo con láser o instrumental tradicional, según tu caso.',
    ],
    benefits: [
      { title: 'Mejor salud bucal', body: 'Elimina bolsas profundas donde se acumulan bacterias.' },
      { title: 'Sonrisa más estética', body: 'Corrige el exceso de encía visible al sonreír.' },
      { title: 'Recuperación rápida', body: 'Técnicas modernas con láser reducen el tiempo de sanación.' },
    ],
    ctaHeadline: 'Encías más sanas y estéticas', ctaBody: 'Agenda una evaluación gratuita para saber si necesitas una gingivectomía.',
  },

  'regeneracion-tisular-y-osea': {
    slug: 'regeneracion-tisular-y-osea', esPath: '/es/regeneracion-tisular-y-osea', enPath: '/guided-tissue-bone-regeneration',
    metaTitle: 'Regeneración Tisular y Ósea Guiada | Fort Worth Dental',
    metaDescription: 'Procedimiento avanzado de regeneración tisular y ósea para restaurar tejido perdido por enfermedad de las encías.',
    schemaName: 'Regeneración Tisular y Ósea',
    h1: 'Regeneración Tisular y Ósea Guiada',
    subtitle: 'Tecnología avanzada que ayuda a regenerar hueso y tejido perdido por enfermedad periodontal severa.',
    introHeadline: 'Recupera lo que la enfermedad periodontal te quitó',
    introParagraphs: [
      'Cuando la enfermedad de las encías ha causado pérdida de hueso o tejido, la regeneración guiada puede revertir parte del daño usando membranas biocompatibles.',
      'Procedimiento especializado realizado por nuestros periodoncistas con años de experiencia.',
    ],
    benefits: [
      { title: 'Regenera hueso perdido', body: 'Restaura la base que sostiene tus dientes naturales.' },
      { title: 'Salva dientes en peligro', body: 'Evita extracciones cuando hay pérdida ósea localizada.' },
      { title: 'Resultados duraderos', body: 'Una vez sanado, el hueso regenerado es permanente.' },
    ],
    ctaHeadline: 'Salva tus dientes naturales', ctaBody: 'Si tienes enfermedad periodontal avanzada, agenda una consulta para evaluar la regeneración.',
  },

  'enfermedad-de-las-encias': {
    slug: 'enfermedad-de-las-encias', esPath: '/es/enfermedad-de-las-encias', enPath: '/gum-disease',
    metaTitle: 'Tratamiento de Enfermedad de las Encías en Fort Worth',
    metaDescription: 'Tratamiento de gingivitis y periodontitis. Detén la pérdida de dientes con tratamiento experto. Consulta hoy.',
    schemaName: 'Tratamiento de Enfermedad de las Encías',
    h1: 'Enfermedad de las Encías',
    subtitle: 'La enfermedad de las encías es la causa #1 de pérdida de dientes en adultos. La detectamos temprano y la tratamos eficazmente.',
    introHeadline: '¿Tienes enfermedad de las encías?',
    introParagraphs: [
      'Las señales tempranas (gingivitis) incluyen sangrado al cepillarse, encías rojas o hinchadas, y mal aliento persistente. Sin tratamiento, progresa a periodontitis y pérdida ósea.',
      'Ofrecemos tratamiento desde limpiezas profundas (raspado y alisado radicular) hasta opciones avanzadas como LANAP láser.',
    ],
    benefits: [
      { title: 'Diagnóstico temprano', body: 'Examen periodontal completo para detectar la etapa de la enfermedad.' },
      { title: 'Tratamiento sin cirugía', body: 'La mayoría de casos se controlan con limpieza profunda no quirúrgica.' },
      { title: 'Opción láser LANAP', body: 'Alternativa mínimamente invasiva a la cirugía periodontal tradicional.' },
    ],
    ctaHeadline: 'No esperes a perder dientes', ctaBody: 'La enfermedad de las encías es tratable cuando se detecta a tiempo. Agenda una evaluación hoy.',
  },

  'odontologia-laser': {
    slug: 'odontologia-laser', esPath: '/es/odontologia-laser', enPath: '/laser-dentistry',
    metaTitle: 'Odontología Láser en Fort Worth | Fort Worth Dental',
    metaDescription: 'Tratamientos dentales con láser — menos dolor, recuperación más rápida, mínimamente invasivo. Tecnología de punta.',
    schemaName: 'Odontología Láser',
    h1: 'Odontología Láser',
    subtitle: 'Tecnología láser para tratamientos dentales más cómodos, precisos y con recuperación más rápida que los métodos tradicionales.',
    introHeadline: '¿Por qué láser en lugar de bisturí?',
    introParagraphs: [
      'La odontología láser es mínimamente invasiva — menos sangrado, menos dolor, recuperación más rápida y menor necesidad de anestesia.',
      'Aplicable en muchos procedimientos: tratamiento de encías (LANAP), endodoncia, frenillo lingual, blanqueamiento y más.',
    ],
    benefits: [
      { title: 'Menos dolor', body: 'Generalmente requiere menos anestesia que los métodos tradicionales.' },
      { title: 'Recuperación más rápida', body: 'Menos inflamación postoperatoria y sanación acelerada.' },
      { title: 'Más preciso', body: 'El láser solo afecta el tejido enfermo, preservando el sano.' },
    ],
    ctaHeadline: 'Tratamiento moderno y cómodo', ctaBody: 'Pregunta por nuestras opciones láser en tu próxima consulta — pueden cambiar tu experiencia dental.',
  },

  'lanap': {
    slug: 'lanap', esPath: '/es/lanap', enPath: '/lanap',
    metaTitle: 'LANAP® Láser en Fort Worth | Alternativa a Cirugía de Encías',
    metaDescription: 'LANAP® — único protocolo láser aprobado por la FDA para tratar enfermedad periodontal. Mínimamente invasivo.',
    schemaName: 'LANAP Láser Periodontal',
    h1: 'LANAP® — Tratamiento Láser de Encías',
    subtitle: 'El único protocolo láser aprobado por la FDA para tratar enfermedad periodontal — alternativa mínimamente invasiva a la cirugía tradicional.',
    introHeadline: '¿Qué es LANAP®?',
    introParagraphs: [
      'LANAP® (Laser-Assisted New Attachment Procedure) es un tratamiento láser revolucionario para enfermedad periodontal severa. Sin bisturí, sin suturas, sin trauma.',
      'Nuestro Dr. Mund ofrece LANAP® como alternativa moderna a la cirugía de encías tradicional — recuperación en 24 horas en lugar de semanas.',
    ],
    benefits: [
      { title: 'Sin bisturí ni suturas', body: 'El láser hace todo el trabajo — sin cortes ni puntos.' },
      { title: 'Recuperación en 24 horas', body: 'Vuelve a tu rutina al día siguiente, no semanas después.' },
      { title: 'Aprobado por la FDA', body: 'Único protocolo láser con aprobación oficial para periodoncia.' },
      { title: 'Salva dientes en peligro', body: 'Resultados clínicos demuestran regeneración real de tejido.' },
    ],
    ctaHeadline: 'Consulta LANAP® GRATIS', ctaBody: 'Si tienes enfermedad periodontal avanzada, descubre cómo LANAP® puede salvar tus dientes naturales sin cirugía.',
  },

  'ajuste-de-mordida': {
    slug: 'ajuste-de-mordida', esPath: '/es/ajuste-de-mordida', enPath: '/occlusal-bite-adjustment',
    metaTitle: 'Ajuste de Mordida en Fort Worth | Fort Worth Dental',
    metaDescription: 'Ajuste oclusal para corregir problemas de mordida y aliviar dolor de mandíbula (TMJ). Procedimiento no invasivo.',
    schemaName: 'Ajuste de Mordida Oclusal',
    h1: 'Ajuste de Mordida',
    subtitle: 'Corrige problemas de mordida que causan desgaste prematuro, dolor de mandíbula y migrañas — procedimiento no invasivo.',
    introHeadline: 'Una mordida equilibrada cambia todo',
    introParagraphs: [
      'Cuando los dientes no se alinean correctamente al cerrar la boca, ciertos puntos reciben presión excesiva. Esto causa fracturas, desgaste, dolor de mandíbula (TMJ) y migrañas.',
      'El ajuste oclusal redistribuye la fuerza de la mordida — un procedimiento simple que puede transformar tu calidad de vida.',
    ],
    benefits: [
      { title: 'Alivia dolor de mandíbula', body: 'Reduce los síntomas de TMJ asociados a mordida desigual.' },
      { title: 'Detiene fracturas', body: 'Distribuye la fuerza para que ningún diente reciba presión excesiva.' },
      { title: 'No invasivo', body: 'Procedimiento simple en una sola visita — sin anestesia.' },
    ],
    ctaHeadline: '¿Dolor de mandíbula?', ctaBody: 'Una mordida desequilibrada puede ser la causa. Evaluación gratuita disponible.',
  },

  'deteccion-de-cancer-oral': {
    slug: 'deteccion-de-cancer-oral', esPath: '/es/deteccion-de-cancer-oral', enPath: '/oral-cancer-screening',
    metaTitle: 'Detección de Cáncer Oral en Fort Worth | Fort Worth Dental',
    metaDescription: 'Detección temprana de cáncer oral en cada limpieza dental. Examen visual completo + VELscope. Salva vidas.',
    schemaName: 'Detección de Cáncer Oral',
    h1: 'Detección de Cáncer Oral',
    subtitle: 'Detección temprana del cáncer oral en cada limpieza — un examen rápido que puede salvar tu vida.',
    introHeadline: 'La detección temprana salva vidas',
    introParagraphs: [
      'El cáncer oral, detectado temprano, tiene una tasa de supervivencia del 90%. Sin embargo, muchos casos se diagnostican tarde porque las primeras señales son sutiles.',
      'En cada limpieza dental incluimos un examen visual de detección — sin costo extra. Si detectamos algo sospechoso, te referimos para biopsia inmediata.',
    ],
    benefits: [
      { title: 'Sin costo extra', body: 'Incluido en cada limpieza dental rutinaria.' },
      { title: 'Detección temprana', body: 'Aumenta dramáticamente las posibilidades de tratamiento exitoso.' },
      { title: 'Rápido y no invasivo', body: 'Examen de 5 minutos al final de tu limpieza.' },
    ],
    ctaHeadline: 'Tu próxima limpieza incluye detección', ctaBody: 'Agenda tu limpieza dental hoy — incluye examen completo de detección de cáncer oral sin costo.',
  },

  'cirugia-osea': {
    slug: 'cirugia-osea', esPath: '/es/cirugia-osea', enPath: '/osseous-surgery',
    metaTitle: 'Cirugía Ósea Periodontal en Fort Worth | Fort Worth Dental',
    metaDescription: 'Cirugía ósea para tratar enfermedad periodontal severa y restaurar la salud de tus encías y hueso.',
    schemaName: 'Cirugía Ósea Periodontal',
    h1: 'Cirugía Ósea Periodontal',
    subtitle: 'Tratamiento quirúrgico para enfermedad periodontal severa — restaura la salud de hueso y encías cuando otros métodos no son suficientes.',
    introHeadline: 'Cuando la enfermedad periodontal es avanzada',
    introParagraphs: [
      'Si la limpieza profunda no es suficiente para detener la enfermedad de las encías, la cirugía ósea elimina el tejido enfermo y reforma el hueso para una recuperación duradera.',
      'Considera primero LANAP® como alternativa mínimamente invasiva — pregúntanos si es apropiado para tu caso.',
    ],
    benefits: [
      { title: 'Detiene la pérdida ósea', body: 'Elimina el tejido infectado que causa la pérdida progresiva.' },
      { title: 'Salva dientes naturales', body: 'Evita extracciones en casos de periodontitis severa.' },
      { title: 'Resultados duraderos', body: 'Con buen mantenimiento postoperatorio, la enfermedad se mantiene controlada.' },
    ],
    ctaHeadline: 'Periodontitis severa requiere atención experta', ctaBody: 'Agenda una consulta especializada — evaluamos si la cirugía ósea o LANAP® es lo mejor para ti.',
  },

  'carillas-de-porcelana': {
    slug: 'carillas-de-porcelana', esPath: '/es/carillas-de-porcelana', enPath: '/porcelain-veneers',
    metaTitle: 'Carillas de Porcelana en Fort Worth | Diseño de Sonrisa',
    metaDescription: 'Carillas de porcelana hechas a la medida — transforma forma, color y tamaño de tus dientes. Sonrisa cosmética experta.',
    schemaName: 'Carillas de Porcelana',
    h1: 'Carillas de Porcelana',
    subtitle: 'Láminas delgadas de porcelana que transforman completamente tu sonrisa — color, forma, tamaño y alineación, todo en pocas visitas.',
    introHeadline: 'Tu sonrisa, completamente rediseñada',
    introParagraphs: [
      'Las carillas de porcelana son la solución cosmética definitiva para sonrisas con dientes manchados, fracturados, desalineados o con espacios. Resultados naturales que duran décadas.',
      'Diseñamos cada carilla específicamente para tu rostro — no hay dos sonrisas iguales porque no hay dos personas iguales.',
    ],
    benefits: [
      { title: 'Transformación completa', body: 'Cambia color, forma y alineación en una sola opción.' },
      { title: 'Apariencia 100% natural', body: 'Porcelana con la traslucidez exacta del esmalte real.' },
      { title: 'Larga duración', body: 'Con buen cuidado duran 15-20 años o más.' },
      { title: 'Resistentes a manchas', body: 'No se manchan con café, vino o tabaco como dientes naturales.' },
    ],
    ctaHeadline: 'Diseño de sonrisa GRATIS', ctaBody: 'Vista previa digital de cómo lucirá tu sonrisa final — sin costo, sin compromiso.',
  },

  'carillas-first-fit': {
    slug: 'carillas-first-fit', esPath: '/es/carillas-first-fit', enPath: '/first-fit-veneers',
    metaTitle: 'Carillas First Fit en Fort Worth | Sonrisa el Mismo Día',
    metaDescription: 'Carillas First Fit — tecnología digital avanzada para una sonrisa nueva en solo 2 horas. Sin múltiples visitas.',
    schemaName: 'Carillas First Fit',
    h1: 'Carillas First Fit',
    subtitle: 'Una sonrisa completamente nueva en solo 2 horas — la última tecnología en odontología cosmética. Sin múltiples visitas.',
    introHeadline: 'La revolución de las carillas',
    introParagraphs: [
      'First Fit es un sistema de carillas digitales avanzadas que se diseñan, fabrican y colocan el mismo día. Olvídate de las múltiples visitas, las carillas temporales y las semanas de espera.',
      'Tecnología 100% digital con escaneo 3D — ajuste perfecto desde la primera prueba.',
    ],
    benefits: [
      { title: 'Una sola visita', body: 'Sonrisa completa en una sola cita de aproximadamente 2 horas.' },
      { title: 'Diseño digital', body: 'Vista previa exacta antes de empezar — aprueba el diseño primero.' },
      { title: 'Sin temporales', body: 'No hay carillas provisionales — pasas directo a las definitivas.' },
      { title: 'Mínimamente invasivo', body: 'Preserva más esmalte natural que las carillas tradicionales.' },
    ],
    ctaHeadline: 'Sonrisa nueva, hoy mismo', ctaBody: 'Consulta gratis para ver si First Fit es ideal para ti — diseño digital incluido.',
  },

  'odontologia-preventiva': {
    slug: 'odontologia-preventiva', esPath: '/es/odontologia-preventiva', enPath: '/preventative-dentistry',
    metaTitle: 'Odontología Preventiva en Fort Worth | Limpiezas Dentales',
    metaDescription: 'Limpiezas dentales, exámenes, radiografías y detección temprana. La base de una salud bucal duradera.',
    schemaName: 'Odontología Preventiva',
    h1: 'Odontología Preventiva',
    subtitle: 'La mejor estrategia dental es prevenir problemas antes de que ocurran — limpiezas, exámenes y educación para toda la familia.',
    introHeadline: 'Prevenir cuesta menos que curar',
    introParagraphs: [
      'Las limpiezas dentales cada 6 meses son la inversión más rentable que puedes hacer en tu salud bucal. Detectamos caries, enfermedad de las encías y otros problemas mientras son fáciles (y económicos) de tratar.',
      'Atendemos a toda la familia — niños desde los 12 meses hasta adultos mayores.',
    ],
    benefits: [
      { title: 'Limpieza profesional', body: 'Removemos sarro y placa que el cepillado no puede.' },
      { title: 'Detección temprana', body: 'Caries y enfermedad de encías detectadas antes de causar daño.' },
      { title: 'Para toda la familia', body: 'Atendemos niños, adultos y adultos mayores con cuidado especializado.' },
      { title: 'Educación personalizada', body: 'Te enseñamos las mejores técnicas de cuidado en casa.' },
    ],
    ctaHeadline: 'Especial $199 para nuevos pacientes', ctaBody: 'Limpieza completa, examen y radiografías por solo $199 (valor de $329).',
  },

  'aumento-de-cresta': {
    slug: 'aumento-de-cresta', esPath: '/es/aumento-de-cresta', enPath: '/ridge-augmentation',
    metaTitle: 'Aumento de Cresta Alveolar | Fort Worth Dental',
    metaDescription: 'Procedimiento de aumento de cresta para reconstruir hueso de la mandíbula y preparar para implantes dentales.',
    schemaName: 'Aumento de Cresta Alveolar',
    h1: 'Aumento de Cresta Alveolar',
    subtitle: 'Reconstruye el hueso de la mandíbula que se ha perdido tras una extracción — esencial para colocar implantes dentales con éxito.',
    introHeadline: 'Restaura la base ósea perdida',
    introParagraphs: [
      'Cuando se extrae un diente y no se reemplaza inmediatamente, el hueso comienza a reabsorberse. El aumento de cresta restaura el volumen óseo necesario para implantes.',
      'Procedimiento especializado realizado con técnicas avanzadas y material biocompatible de alta calidad.',
    ],
    benefits: [
      { title: 'Permite implantes futuros', body: 'Restaura el volumen óseo necesario para colocar implantes estables.' },
      { title: 'Preserva la estética facial', body: 'Evita el aspecto hundido que causa la pérdida ósea prolongada.' },
      { title: 'Resultados duraderos', body: 'El hueso regenerado es estable y permanente.' },
    ],
    ctaHeadline: '¿Perdiste un diente hace tiempo?', ctaBody: 'Evaluación gratuita para determinar si necesitas aumento de cresta antes del implante.',
  },

  'preservacion-de-cresta': {
    slug: 'preservacion-de-cresta', esPath: '/es/preservacion-de-cresta', enPath: '/ridge-preservation',
    metaTitle: 'Preservación de Cresta Alveolar | Fort Worth Dental',
    metaDescription: 'Preservación inmediata de cresta tras extracción dental. Mantén el hueso intacto para futuros implantes.',
    schemaName: 'Preservación de Cresta',
    h1: 'Preservación de Cresta Alveolar',
    subtitle: 'Tras una extracción, la preservación inmediata mantiene el hueso intacto — la mejor estrategia para implantes futuros.',
    introHeadline: 'Actúa antes de que se pierda el hueso',
    introParagraphs: [
      'Cuando se extrae un diente, el hueso comienza a reabsorberse en cuestión de semanas. La preservación de cresta es un injerto inmediato que evita esa pérdida.',
      'Procedimiento simple realizado al momento de la extracción — agrega solo 5-10 minutos al procedimiento.',
    ],
    benefits: [
      { title: 'Preserva el hueso desde el día 1', body: 'Evita la reabsorción que ocurre tras una extracción sin tratamiento.' },
      { title: 'Facilita implantes futuros', body: 'Mantiene el volumen óseo para colocar implantes sin injertos adicionales.' },
      { title: 'Procedimiento simple', body: 'Se hace al momento de la extracción — sin visitas extra.' },
    ],
    ctaHeadline: '¿Necesitas una extracción?', ctaBody: 'Pregunta por la preservación de cresta — la mejor decisión si planeas un implante futuro.',
  },

  'limpieza-profunda': {
    slug: 'limpieza-profunda', esPath: '/es/limpieza-profunda', enPath: '/scaling-root-planing',
    metaTitle: 'Limpieza Dental Profunda en Fort Worth | Fort Worth Dental',
    metaDescription: 'Raspado y alisado radicular — tratamiento no quirúrgico para enfermedad de las encías. Detiene la progresión.',
    schemaName: 'Limpieza Dental Profunda',
    h1: 'Limpieza Dental Profunda (Raspado y Alisado Radicular)',
    subtitle: 'Tratamiento no quirúrgico para gingivitis y periodontitis temprana — limpia bajo la encía donde el cepillado no llega.',
    introHeadline: 'El primer paso contra la enfermedad de las encías',
    introParagraphs: [
      'El raspado y alisado radicular (limpieza profunda) elimina la placa y el sarro endurecido bajo la línea de la encía. Es el tratamiento estándar para gingivitis y periodontitis temprana.',
      'No es lo mismo que una limpieza regular — es más profundo, más detallado y a veces requiere anestesia local para tu comodidad.',
    ],
    benefits: [
      { title: 'No quirúrgico', body: 'Detiene la enfermedad de las encías sin cirugía.' },
      { title: 'Reduce las bolsas periodontales', body: 'Las encías sanan y se adhieren mejor a los dientes.' },
      { title: 'Previene pérdida de dientes', body: 'Si se hace a tiempo, salva dientes que estarían en peligro.' },
    ],
    ctaHeadline: '¿Encías que sangran?', ctaBody: 'No ignores las señales — agenda una limpieza profunda y detén la enfermedad antes de que avance.',
  },

  'sedacion-dental': {
    slug: 'sedacion-dental', esPath: '/es/sedacion-dental', enPath: '/sedation',
    metaTitle: 'Sedación Dental en Fort Worth | Atención Sin Ansiedad',
    metaDescription: 'Sedación oral y óxido nitroso para una experiencia dental cómoda y sin ansiedad. Perfecto para pacientes nerviosos.',
    schemaName: 'Sedación Dental',
    h1: 'Sedación Dental',
    subtitle: 'Para pacientes con ansiedad dental — sedación oral o óxido nitroso para una experiencia completamente cómoda.',
    introHeadline: '¿Te da miedo el dentista?',
    introParagraphs: [
      'No estás solo. Aproximadamente 1 de cada 5 adultos evita el dentista por ansiedad. La sedación dental hace que tu cita sea cómoda, relajante y a veces ni siquiera la recuerdes.',
      'Ofrecemos varias opciones según tu nivel de ansiedad y el procedimiento — desde óxido nitroso ligero hasta sedación oral más profunda.',
    ],
    benefits: [
      { title: 'Sin ansiedad', body: 'Te sientes relajado y cómodo durante todo el procedimiento.' },
      { title: 'Múltiples opciones', body: 'Desde óxido nitroso (gas de risa) hasta sedación oral profunda.' },
      { title: 'Más trabajo en menos visitas', body: 'Bajo sedación podemos completar varios procedimientos en una sola cita.' },
      { title: 'Seguro y supervisado', body: 'Monitoreo constante de signos vitales durante todo el procedimiento.' },
    ],
    ctaHeadline: 'Vuelve al dentista sin miedo', ctaBody: 'Habla con nosotros sobre tus preocupaciones — encontraremos la opción de sedación ideal para ti.',
  },
};

export const SPANISH_SERVICE_SLUGS = Object.keys(SPANISH_SERVICES);
